import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FileTextIcon, MailIcon, PrinterIcon, RotateCcwIcon, UserIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchOrder } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { MissingItem } from '../ui/MissingItem';
import { LoadFailed } from '../ui/LoadState';
import { Spinner } from '../../ui/Spinner';
import { btnDestructive, btnSecondary } from '../ui/styles';
import { paymentLabel, received } from '../inbox/inboxRules';
import { RefundForm } from './RefundForm';
import { CancelForm } from './CancelForm';
import { OrderTimeline } from './OrderTimeline';
import { FulfilmentPanel } from './FulfilmentPanel';
import { creditNumber, printCreditNote, printOrderInvoice, printOrderPackingSlip } from './orderDocuments';
import { channelLabel, orderBadge, orderNumber, refundLabel, vatBreakdown } from './orderLabels';
import { money } from '../../core/format/currency';
import { cancelProblem, paymentMethodLabel, refundableCents } from '../../core/rules/revenue';
import { formatStreet } from '../../core/rules/contact';
import { trackingUrl } from '../../core/rules/tracking';
import { dayShort } from '../../core/format/dates';
import { groupByBundle } from '../../core/rules/orderBundles';
import type { AdminOrder } from '../../core/types/adminRequests';
import type { RequestOptions } from '../../core/types/backend';
type Action = 'refund' | 'cancel' | null;

/**
 * Eén bestelling: wat erin zat (met de btw zoals verkocht), het adres, de betaling, de tijdlijn en
 * de terugbetalingen. Terugbetalen en annuleren wachten altijd op de server (transactiescherm).
 */
export function OrderDetail() {
  const {
    id = ''
  } = useParams();
  // Vanuit de lijst: terug met dezelfde filters. Vanuit de inbox: terug naar de inbox.
  const {
    state
  } = useLocation() as {
    state: {
      from?: string;
      back?: {
        to: string;
        label: string;
      };
    } | null;
  };
  const back = state?.back ?? {
    to: `/be/bestellingen${state?.from ?? ''}`,
    label: 'Alle bestellingen'
  };
  const load = useCallback((opts: RequestOptions) => fetchOrder(id, opts), [id]);
  const {
    data: order,
    setData,
    status,
    reload,
    refresh
  } = useLatestLoad(load);
  const [action, setAction] = useState<Action>(null);
  if (status === 'loading' && !order) return <div role="status" className="flex items-center gap-2 py-16 text-[14px] text-muted" data-id="element-3377" data-mp="1ykm5h3jpizep">
        <Spinner data-id="element-3378" data-mp="1tnc4qrqvb1bk" /> Bestelling laden…
      </div>;
  if (status === 'error' && !order) return <LoadFailed title="Deze bestelling kon niet laden" onRetry={reload} data-id="element-3379" data-mp="1plqvrhhgf76n" />;
  if (!order) return <MissingItem title="Bestelling niet gevonden" back={back} data-id="element-3380" data-mp="3nzah32kh4dbo" />;
  const done = (next: AdminOrder, message: string) => {
    setData(next);
    setAction(null);
    toast.success(message);
  };
  return <OrderView order={order} back={back} action={action} setAction={setAction} onRefunded={next => done(next, 'Terugbetaald. De klant krijgt een mail van Mollie.')} onCancelled={next => done(next, 'Bestelling geannuleerd')} onFailed={refresh} onFulfilled={refresh} data-id="element-3381" data-mp="15q6580lc0rdt" />;
}
type ViewProps = {
  order: AdminOrder;
  back: {
    to: string;
    label: string;
  };
  action: Action;
  setAction: (a: Action) => void;
  onRefunded: (o: AdminOrder) => void;
  onCancelled: (o: AdminOrder) => void;
  onFailed: () => void;
  onFulfilled: () => void;
};
function OrderView({
  order: o,
  back,
  action,
  setAction,
  onRefunded,
  onCancelled,
  onFailed,
  onFulfilled
}: ViewProps) {
  const {
    site,
    index
  } = useAdmin();
  const badge = orderBadge(o);
  const paid = o.paymentStatus === 'betaald';
  const left = refundableCents(o);
  const canRefund = paid && left > 0 && !o.cancelledAt;
  const canCancel = cancelProblem(o) === null;
  const vat = vatBreakdown(o);
  const a = o.address;

  // Het formulier opent onder de regels; op de telefoon valt dat buiten beeld.
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (action) formRef.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [action]);
  const invoice = async () => {
    if (!(await printOrderInvoice(o, site.salon, dayShort(o.placedAt.slice(0, 10))))) toast.error('Factuur openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  const slip = async () => {
    if (!(await printOrderPackingSlip(o, index.productById, site.salon, id => index.routineById(id)?.name))) toast.error('Pakbon afdrukken lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div data-id="element-3382" data-mp="3cf7u9xiqs4fw">
      <PageHeader title={o.number ? `Bestelling ${o.number}` : 'Bestelling zonder nummer'} description={`${o.customerName || o.email} · ${received(o.placedAt)} · ${channelLabel[o.channel]} · ${money(o.totalCents)}`} back={back} actions={<>
            <Badge tone={badge.tone} data-id="element-3384" data-mp="3w0zux8pus3oz">{badge.label}</Badge>
            {paid && <button type="button" onClick={() => void invoice()} className={btnSecondary} data-id="element-3385" data-mp="3vyni19yycjis">
                <FileTextIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3386" data-mp="3i3bau33m10kz" />
                Factuur
              </button>}
            {paid && !o.cancelledAt && <button type="button" onClick={() => void slip()} className={btnSecondary} data-id="element-3387" data-mp="38a46mlklvqmh">
                <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3388" data-mp="3d3gl42vkmh1i" />
                Pakbon
              </button>}
          </>} data-id="element-3383" data-mp="3ceyc2w3xv1kr" />

      <div className="grid gap-6 lg:grid-cols-3" data-id="element-3389" data-mp="33ddu2880fd5t">
        <div className="space-y-6 lg:col-span-2" data-id="element-3390" data-mp="339zuf7qqdi22">
          <FulfilmentPanel order={o} onChanged={onFulfilled} data-id="element-3391" data-mp="2pdw0zgnoldqn" />
          <Panel title="Wat er besteld is" flush data-id="element-3392" data-mp="2ubeb3adfpz08">
            <table className="w-full text-[14px]" data-id="element-3393" data-mp="2ub4nftfv43xp">
              <caption className="sr-only" data-id="element-3394" data-mp="zv5y8erb9jyj">Bestelregels met btw</caption>
              <thead className="text-[12px] text-muted" data-id="element-3395" data-mp="zxa9xivgbn9d">
                <tr className="border-b border-line" data-id="element-3396" data-mp="v3xlp9uopih5">
                  <th scope="col" className="px-4 py-2 text-left font-normal sm:px-5" data-id="element-3397" data-mp="q5me75rv3k23">Product</th>
                  <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-3398" data-mp="m415lwmd1rxp">Aantal</th>
                  <th scope="col" className="hidden px-2 py-2 text-right font-normal sm:table-cell" data-id="element-3399" data-mp="m27xecw3mznl">Btw</th>
                  <th scope="col" className="px-4 py-2 text-right font-normal sm:px-5" data-id="element-3400" data-mp="1dv75qd22zpmz">Bedrag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-3401" data-mp="19skd49xl60oq">
                {/* Een protocol als bundel onder één kopje: dan pakt Sarah het als set in. */}
                {groupByBundle(o.lines, l => l.bundleRoutineId).flatMap(g => [...(g.routineId ? [<tr key={`bundle-${g.routineId}`} className="bg-sand/60" data-id="element-3402" data-mp="3pjq4uj7ym4nh">
                          <th scope="rowgroup" colSpan={4} className="px-4 py-2 text-left text-[12px] font-normal text-clay sm:px-5" data-id="element-3403" data-mp="253i70xco9jk6">
                            Protocol · {index.routineById(g.routineId)?.name ?? g.routineId} · {g.lines.length} producten
                          </th>
                        </tr>] : []), ...g.lines.map(l => <tr key={l.productId} data-id="element-3404" data-mp="2ntzzd0tled50">
                      <td className={`py-3 pr-4 text-ink sm:pr-5 ${g.routineId ? 'pl-7 sm:pl-8' : 'pl-4 sm:pl-5'}`} data-id="element-3405" data-mp="2nvaygnc3ewps">
                        {l.name}
                        <span className="block text-[12px] text-muted sm:hidden" data-id="element-3406" data-mp="2xr4mb2v9icmv">btw {l.vatRate}%</span>
                      </td>
                      <td className="px-2 py-3 text-right tabular-nums" data-id="element-3407" data-mp="31sg44m7w9tki">{l.qty}</td>
                      <td className="hidden px-2 py-3 text-right tabular-nums text-muted sm:table-cell" data-id="element-3408" data-mp="13eoi07m118kn">{l.vatRate}%</td>
                      <td className="px-4 py-3 text-right tabular-nums text-ink sm:px-5" data-id="element-3409" data-mp="17f8dl6qyc44o">{money(l.lineTotalCents)}</td>
                    </tr>)])}
              </tbody>
            </table>
            <dl className="space-y-1.5 border-t border-line px-4 py-4 text-[13px] sm:px-5" data-id="element-3410" data-mp="jhsjsftc90vf">
              <div className="flex justify-between gap-4" data-id="element-3411" data-mp="xcuo4yijei64">
                <dt className="text-muted" data-id="element-3412" data-mp="tapwg5bnfvm4">Verzending (btw {o.shippingVatRate}%)</dt>
                <dd className="tabular-nums text-ink" data-id="element-3413" data-mp="5j2faf46vyx1">{o.shippingCents ? money(o.shippingCents) : 'Gratis'}</dd>
              </div>
              {vat.map(v => <div key={v.vatRate} className="flex justify-between gap-4" data-id="element-3414" data-mp="ekimyf3mdmxk">
                  <dt className="text-muted" data-id="element-3415" data-mp="eifn73co0ffn">Waarvan btw {v.vatRate}%</dt>
                  <dd className="tabular-nums text-muted" data-id="element-3416" data-mp="9knz6kf55dgq">{money(v.vatCents)}</dd>
                </div>)}
              <div className="flex justify-between gap-4 pt-1.5 text-[15px]" data-id="element-3417" data-mp="1pxumidscjjdx">
                <dt className="text-ink" data-id="element-3418" data-mp="1kzt1vof9vc0c">Totaal</dt>
                <dd className="tabular-nums text-ink" data-id="element-3419" data-mp="1thv0kxq5zc5b">{money(o.totalCents)}</dd>
              </div>
              {o.voucherCents > 0 && <div className="flex justify-between gap-4" data-id="element-3420" data-mp="1xjzs9qx1xypb">
                  <dt className="text-muted" data-id="element-3421" data-mp="1jjp1envqsk45">Betaald met cadeaubon (gaat niet via Mollie terug)</dt>
                  <dd className="shrink-0 tabular-nums text-ink" data-id="element-3422" data-mp="1oia02ivcivqh">{money(o.voucherCents)}</dd>
                </div>}
              {o.refunds.map(r => <div key={r.id} className="flex justify-between gap-4" data-id="element-3423" data-mp="2ca7bw9yspnrl">
                  <dt className="min-w-0 text-muted" data-id="element-3424" data-mp="2h5w7613s8ii3">
                    {refundLabel(r)} {received(r.at)} · <span className="text-ink" data-id="element-3425" data-mp="22hi35mumqnzy">{r.reason}</span>
                    {r.restocked && <span data-id="element-3426" data-mp="27gmccdhk0lqa"> · terug op voorraad</span>}
                    {r.voucherRestoredCents ? <span data-id="element-3427" data-mp="2v78ke4rk660a"> · {money(r.voucherRestoredCents)} terug op cadeaubon</span> : null}
                    <button type="button" onClick={() => void printCreditNote(o, r, site.salon).then(ok => {
                  if (!ok) toast.error('Creditnota openen lukte niet', {
                    description: 'Probeer het opnieuw of gebruik een andere browser.'
                  });
                })} className="ml-2 text-ink underline decoration-line underline-offset-4 hover:decoration-ink" data-id="element-3428" data-mp="2zxp05viuq3pt">
                      Creditnota {creditNumber(o, r)}
                    </button>
                  </dt>
                  <dd className="shrink-0 tabular-nums text-clay" data-id="element-3429" data-mp="1e1zjuukl9us2">−{money(r.amountCents)}</dd>
                </div>)}
            </dl>
            {(o.gift || o.code) && <p className="border-t border-line px-4 py-3 text-[13px] text-muted sm:px-5" data-id="element-3430" data-mp="14bn034klm5mz">
                {o.gift && <>
                    Cadeau erbij: <span className="text-ink" data-id="element-3431" data-mp="qcx1fki4rxwi">{o.gift}</span>
                  </>}
                {o.gift && o.code && ' · '}
                {o.code && <>
                    Code: <span className="text-ink" data-id="element-3432" data-mp="briyoyiwps7q">{o.code}</span>
                  </>}
              </p>}
          </Panel>

          <div ref={formRef} className="scroll-mb-24" data-id="element-3433" data-mp="lnw3arfzpqh9">
            {action === 'refund' ? <RefundForm order={o} onDone={onRefunded} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3434" data-mp="gnzxqnrn9qzl" /> : action === 'cancel' ? <CancelForm order={o} onDone={onCancelled} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3435" data-mp="umheg1j4u380" /> : (canRefund || canCancel) && <div className="flex flex-wrap items-center gap-2" data-id="element-3436" data-mp="14csn21drh0l8">
                  {canRefund && <button type="button" onClick={() => setAction('refund')} className={btnSecondary} data-id="element-3437" data-mp="zfizysduh10k">
                      <RotateCcwIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3438" data-mp="1dfbkg5ys2l03" />
                      Terugbetalen
                    </button>}
                  {canCancel && <button type="button" onClick={() => setAction('cancel')} className={`${btnDestructive} sm:ml-auto`} data-id="element-3439" data-mp="1geaewzrazpba">
                      <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3440" data-mp="1qektooszl4sk" />
                      Bestelling annuleren
                    </button>}
                </div>}
          </div>
          {!canCancel && !o.cancelledAt && paid && o.fulfilment !== 'nieuw' && action === null && <p className="-mt-3 text-[12px] text-muted" data-id="element-3441" data-mp="244o9awxd2091">Al ingepakt of verzonden: annuleren kan niet meer, terugbetalen wel.</p>}
        </div>

        <div className="space-y-6" data-id="element-3442" data-mp="2e45b339hrtzq">
          <Panel title="Klant" data-id="element-3443" data-mp="2e3400ysh77i0">
            <p className="text-[14px] text-ink" data-id="element-3444" data-mp="2e3dqsolchior">{o.customerName || '—'}</p>
            <p className="break-all text-[13px] text-muted" data-id="element-3445" data-mp="escnfx8lg0zo">{o.email}</p>
            <address className="mt-3 text-[13px] not-italic leading-snug text-ink" data-id="element-3446" data-mp="jpmaj68ig0kc">
              {`${a.firstName} ${a.lastName}`.trim() || o.customerName}
              <br data-id="element-3447" data-mp="2x2id3bg4yniy" />
              {formatStreet(a)}
              <br data-id="element-3448" data-mp="2s9fir13jba1p" />
              {a.postcode} {a.city}
              {a.country === 'BE' && ' · België'}
            </address>
            {o.skinNote && <p className="mt-3 rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-3449" data-mp="2o5prg3vh6ecz">
                <span className="block text-[12px] text-muted" data-id="element-3450" data-mp="2j6whglfed5hk">Notitie voor jou</span>“{o.skinNote}”
              </p>}
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-3451" data-mp="3bxy0wyq4hn6w">
              {o.customerId ? <Link to={`/be/klanten/${o.customerId}?tab=bestellingen`} className={btnSecondary} data-id="element-3452" data-mp="36yblcimtnuux">
                  <UserIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3453" data-mp="31zquvaxdgpjo" />
                  Dossier
                </Link> : <span className="text-[12px] text-muted" data-id="element-3454" data-mp="1vkpxr1wm21bi">Gastbestelling</span>}
              <a href={`mailto:${o.email}?subject=${encodeURIComponent(`Je bestelling ${orderNumber(o)}`)}`} className={btnSecondary} data-id="element-3455" data-mp="1qiquds0vf4ek">
                <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3456" data-mp="3j7q4r3ok2nza" />
                Mail
              </a>
            </div>
          </Panel>

          <Panel title="Betaling" data-id="element-3457" data-mp="35hwh2oose9r5">
            <dl className="space-y-1.5 text-[13px]" data-id="element-3458" data-mp="35eig9kvru8lt">
              <div className="flex justify-between gap-4" data-id="element-3459" data-mp="3ag7q4x79cg8b">
                <dt className="text-muted" data-id="element-3460" data-mp="1zw5z0nxpo9b">Status</dt>
                <dd className="text-ink" data-id="element-3461" data-mp="204ll0bfz7o7">{paymentLabel[o.paymentStatus]}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-3462" data-mp="60ogrycgg17p">
                <dt className="text-muted" data-id="element-3463" data-mp="3o7cjjhk18ca7">Methode</dt>
                <dd className="text-ink" data-id="element-3464" data-mp="3o8xeh8a9dx8l">{paymentMethodLabel[o.paymentMethod]}</dd>
              </div>
              {o.refundedCents > 0 && <div className="flex justify-between gap-4" data-id="element-3465" data-mp="2cr4l9sbmvn4c">
                  <dt className="text-muted" data-id="element-3466" data-mp="2cu0mbpis2rx0">Nog terug te betalen</dt>
                  <dd className="tabular-nums text-ink" data-id="element-3467" data-mp="2hsbxc3kt78gt">{money(left)}</dd>
                </div>}
              {o.tracking && <div className="flex justify-between gap-4" data-id="element-3468" data-mp="25855dwndsdpq">
                  <dt className="text-muted" data-id="element-3469" data-mp="257n4gmprxbbh">Track & trace</dt>
                  <dd data-id="element-3470" data-mp="2j8669c3nbl57">
                    <a href={trackingUrl(o.tracking)} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2" data-id="element-3471" data-mp="2j6mmhbis6ryo">
                      {o.tracking.carrier} {o.tracking.code}
                      <span className="sr-only" data-id="element-3472" data-mp="1qjsal30150wt"> (opent in een nieuw tabblad)</span>
                    </a>
                  </dd>
                </div>}
            </dl>
          </Panel>

          <Panel title="Tijdlijn" data-id="element-3473" data-mp="20amxm9l3o4jb">
            <OrderTimeline events={o.timeline} data-id="element-3474" data-mp="1vibwx4ijcepp" />
          </Panel>
        </div>
      </div>
    </div>;
}